import React from "react";
import { UserPaymentTrancheApi, userPaymentsApi } from "api";
import {
  PaziaButton,
  PaziaSelect,
  PaziaFormButton,
  PaziaCheckbox,
  PaziaDateSelect,
  PaziaTextarea,
  Select,
} from "components/atoms";
import { PaziaModal } from "components/molecules";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS, REGISTRATION_TRANCHE_PAYMENT_STATUSES } from "consts";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

interface EditTrancheStatusModalProps {
  onCloseRequest: () => void;
  tranche?: UserPaymentTrancheApi;
  userId: number;
  courseId: number;
}
import { getApiErrorMessage, useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";

export const EditTrancheStatusModal = ({ onCloseRequest, tranche, userId, courseId }: EditTrancheStatusModalProps) => {
  const { showError, showSuccess } = useToaster();

  const onSubmit = async (data: FormValues) => {
    try {
      if (!tranche) return;

      // Include salesDate and comment in the request body when status is PAYED_OUTSIDE_SYSTEM
      const requestBody: {
        sendEmail: boolean;
        generateInvoice: boolean;
        status: number;
        salesDate?: Date;
        comment?: string;
      } = {
        sendEmail: data.sendEmail,
        generateInvoice: data.generateInvoice,
        status: data.status,
      };

      if (data.status === REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAYED_OUTSIDE_SYSTEM) {
        if (data.salesDate) {
          requestBody.salesDate = data.salesDate;
        }
        if (data.comment) {
          requestBody.comment = data.comment;
        }
      }

      await userPaymentsApi.changeStatus(userId, courseId, tranche.courseTrancheId, requestBody);
      showSuccess("Status płatności został zmieniony");
    } catch (error) {
      console.error(error);
      showError(getApiErrorMessage(error, "Wystąpił błąd podczas zmiany statusu płatności"));
    } finally {
      onCloseRequest();
    }
  };

  return (
    <PaziaModal id="edit-tranche-status-modal" onCloseRequest={onCloseRequest} isOpen={!!tranche} size="2xl">
      {tranche && (
        <ModalPadding>
          <ModalHeader>Edytuj status płatności</ModalHeader>
          <div className="flex flex-col gap-4">
            <Select
              disabled
              name="currentStatus"
              label="Aktualny status"
              options={REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS}
              value={tranche.status}
            />
            <EditTrancheStatusForm onSubmit={onSubmit} onCancel={onCloseRequest} />
          </div>
        </ModalPadding>
      )}
    </PaziaModal>
  );
};

const EditTrancheStatusForm = ({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: FormValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const schema = yup
    .object({
      status: yup.number().required(),
      sendEmail: yup.boolean().default(false).required(),
      generateInvoice: yup.boolean().default(true).required(),
      salesDate: yup.date().when("status", {
        is: REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAYED_OUTSIDE_SYSTEM,
        then: (schema) => schema.required("Data sprzedaży jest wymagana"),
        otherwise: (schema) => schema.nullable(),
      }),
      comment: yup.string().nullable(),
    })
    .required();

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      salesDate: new Date(),
      comment: "",
      generateInvoice: true,
    },
  });
  const { handleSubmit, watch } = methods;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
        <PaziaSelect
          label="Nowy status"
          name="status"
          options={REGISTRATION_TRANCHE_PAYMENT_STATUSES_OPTIONS.filter((option) =>
            possibleChoices.includes(option.value),
          )}
          className="mb-2"
        />
        {watch("status") === REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAYED_OUTSIDE_SYSTEM && (
          <>
            <PaziaDateSelect name="salesDate" label="Data sprzedaży" className="mb-2" />
            <PaziaTextarea name="comment" label="Komentarz" className="mb-2" />
          </>
        )}
        <PaziaCheckbox name="sendEmail" label="Wyślij powiadomienie o zmianie statusu" />
        <PaziaCheckbox name="generateInvoice" label="Generuj fakturę" />
        <div className="mt-4 flex justify-end gap-2">
          <PaziaButton btnType="button" type="tertiary" onClick={onCancel} title="Anuluj" />
          <PaziaFormButton type="success" title="Zapisz" />
        </div>
      </form>
    </FormProvider>
  );
};
const possibleChoices = [
  REGISTRATION_TRANCHE_PAYMENT_STATUSES.NOT_PAID,
  REGISTRATION_TRANCHE_PAYMENT_STATUSES.SKIPPED_BY_ADMIN,
  REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAYED_OUTSIDE_SYSTEM,
] as number[];
type FormValues = {
  status: number;
  sendEmail: boolean;
  generateInvoice: boolean;
  salesDate?: Date;
  comment?: string | null;
};
